import { all, call, put, putResolve, select } from "redux-saga/effects";
import {
  ADD_PASSPORT,
  ADD_TRAVELER,
  CallState,
  CorpSessionInfo,
  ICorpPerson,
  IPerson,
  Person,
  SAVED_NEW_TRAVELER,
  SavedNewTravelerProperties,
  SessionInfo,
} from "redmond";
import isEqual from "lodash-es/isEqual";
import updateUserDefaultTraveler from "b2b-base/src/api/v1/user/updateUserDefaultTraveler";
import { isCorpTenant } from "@capone/common";
import {
  CorpUsersListResponse,
  fetchCorpUsersList,
} from "b2b-base/src/api/v1/user/fetchCorpUsersList";
import updateUserShowProfile from "b2b-base/src/api/v1/user/updateUserShowProfile";
import { ToggleActions } from "halifax";
import { buildCorpTravelers } from "b2b-base/src/utils/capone-corporate/travelersMappingUtils";
import updateUserHideProfile from "b2b-base/src/api/v1/user/updateUserHideProfile";
import { useCorpPersonData } from "@capone/common/src/hooks/useCorpPersonData";
import { actions } from "../../actions";
import { updateUserPassenger } from "../../../../api/v0/book/user-passengers/updateUserPassenger";
import {
  fetchUserPassengers,
  IListUserPassengersResponse,
} from "../../../../api/v0/book/user-passengers/fetchUserPassengers";
import {
  getUserPassengers,
  getUserSelectedPassengerIds,
  getUserSelectedLapInfantIds,
  getTripFinalFlightDate,
} from "../../reducer";
import { IStoreState } from "../../../../reducers/types";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { getViewedTripSummaryProperties } from "../../../shop/reducer";
import { addTrackingProperties } from "../../../../context/experiments";
import { config } from "../../../../api/config";
import fetchUserInfo from "../../../../api/v0/user/fetchUserInfo";
import { getIsLapInfant } from "../../components/FlightBookPassengerSelection/useFlightBookPassengerSelect";
import { forkTravelerBusinessSync } from "b2b-base/src/api/v1/user-passengers/forkTravelerBusinessSync";

export function* updateUserPassengerSaga(action: actions.IUpdateUserPassenger) {
  try {
    // Check if passport field is valid or filter it
    // & filter driverLicense field
    // ^ Adding back driverLicense field as part of BOPS-628
    const { person, source, hideProfileAction } = action.payload;
    const { passport, ...restOfPayload } = person;
    const entryPoint = action.entryPoint;
    const personToUpdate: Person = { ...restOfPayload };
    if (passport?.countryOfIssue?.length === 2) {
      personToUpdate.passport = passport;
    }

    const state: IStoreState = yield select();
    const currentTravelers = getUserPassengers(state);
    const travelerIds = getUserSelectedPassengerIds(state);
    const lapInfantIds = getUserSelectedLapInfantIds(state);
    const viewedTripSummaryProperties = getViewedTripSummaryProperties(state);
    const existingTraveler = currentTravelers.find(
      (traveler) => traveler.id === personToUpdate.id
    );
    const isNewTraveler: boolean = !existingTraveler;
    const [shouldAssignTraveler, deUserId] = useCorpPersonData(
      "isGhostUser" in person ? (person as ICorpPerson) : null
    );
    const tripFinalFlightDate = getTripFinalFlightDate(state);
    const isLapInfant = getIsLapInfant(personToUpdate, tripFinalFlightDate);

    const shouldValidatePassengersOnUpdate =
      existingTraveler && !isLapInfant && isCorpTenant(config.TENANT);
    // remove undefined keys for comparison
    Object.keys(personToUpdate).forEach(
      (key) => personToUpdate[key] === undefined && delete personToUpdate[key]
    );
    const sameTravelerInfo: boolean = isEqual(existingTraveler, personToUpdate);

    if (!sameTravelerInfo) {
      yield updateUserPassenger({
        person: personToUpdate,
        isNewTraveler,
        previousPerson: existingTraveler,
        source,
        hideProfileAction,
        entryPoint,
      });
    }

    if (shouldAssignTraveler) {
      yield updateUserDefaultTraveler({
        travelerId: personToUpdate.id,
        userId: deUserId,
      });
    }

    if (hideProfileAction === ToggleActions.OFF) {
      yield updateUserShowProfile({
        travelerId: personToUpdate.id,
      });
    } else if (hideProfileAction === ToggleActions.ON) {
      yield updateUserHideProfile({
        travelerId: personToUpdate.id,
      });
    }

    const sessionInfo: SessionInfo = yield call(fetchUserInfo);
    const canViewUsers =
      "corporateInfo" in sessionInfo
        ? (sessionInfo as CorpSessionInfo).corporateInfo.permissions
            .canViewUsers
        : false;

    const travelerDefaultTravelerId =
      ("corporateInfo" in sessionInfo &&
        (sessionInfo as CorpSessionInfo).corporateInfo.defaultTravelerId) ??
      "";

    let userPassengers: IPerson[] | ICorpPerson[];
    if (!isCorpTenant(config.TENANT)) {
      userPassengers = yield fetchUserPassengers({});
    } else {
      const [corpUsersList, caponeUserPassengers]: [
        CorpUsersListResponse,
        IListUserPassengersResponse
      ] = yield all([
        canViewUsers
          ? call(fetchCorpUsersList)
          : {
              users: [
                {
                  id: sessionInfo.userId,
                  firstName: sessionInfo.userInfo.firstName,
                  lastName: sessionInfo.userInfo.lastName,
                  defaultTravelerId: travelerDefaultTravelerId,
                },
              ],
            },
        call(fetchUserPassengers, {}),
      ]);

      yield* forkTravelerBusinessSync(sessionInfo, corpUsersList, personToUpdate);

      const hiddenTravelerIds =
        "corporateInfo" in sessionInfo
          ? (sessionInfo as CorpSessionInfo).corporateInfo.hiddenTravelerIds
          : [];

      const loggedInUserId = sessionInfo.userId;

      userPassengers = buildCorpTravelers(
        caponeUserPassengers,
        corpUsersList.users,
        hiddenTravelerIds,
        loggedInUserId
      );
    }

    if (currentTravelers.length < userPassengers.length) {
      trackEvent({
        eventName: ADD_TRAVELER,
        properties: {},
      });
    }

    if (action.isNewTraveler) {
      trackEvent({
        eventName: SAVED_NEW_TRAVELER,
        properties: addTrackingProperties(undefined, {
          entry_type: "checkout",
        } as SavedNewTravelerProperties),
      });
    }

    const shouldValidateInSaga = shouldValidatePassengersOnUpdate &&
      travelerIds.includes(personToUpdate.id);

    if (shouldValidateInSaga) {
      yield put(actions.validatePassengers(travelerIds, lapInfantIds));
    }

    yield putResolve(
      actions.setUserPassengers({
        userPassengerCallState: CallState.Success,
        userPassengers,
      })
    );

    if (action.updatePassport) {
      trackEvent({
        eventName: ADD_PASSPORT,
        properties: { success: true },
      });
      // if keepBookingErrors = true, don't resetBookingErrors
      // - to allow all passports to be updated before closing modal
      // - modal open is dependent on getPriceQuoteErrors
      if (!action.keepBookingErrors) {
        yield put(actions.resetBookingErrors());
      }
    }
    if (action.autoSelect) {
      if (isNewTraveler) {
        yield put(
          actions.setUserSelectedPassengerIds({
            userSelectedPassengerIds: [
              ...travelerIds,
              action.payload.person.id,
            ],
            multiTicketType: viewedTripSummaryProperties?.multi_ticket_type,
          })
        );
      }
      yield put(
        actions.validatePassengers(
          [...travelerIds, action.payload.person.id],
          lapInfantIds
        )
      );
    }
  } catch (e) {
    if (action.updatePassport) {
      trackEvent({
        eventName: ADD_PASSPORT,
        properties: { success: false },
      });
    }
    yield put(actions.setUserPassengersCallStateFailed());
  }
}
